const speechFormatCategories = ['number', 'text', 'datetime', 'contact'] as const;

type SpeechFormatCategory = (typeof speechFormatCategories)[number];

type SpeechFormat = {
  id: string;
  label: string;
  description: string;
  example: string;
  category: SpeechFormatCategory;
};

const speechFormats: SpeechFormat[] = [
  {
    id: 'digits',
    label: 'Digits',
    description: 'Read each digit individually',
    example: '12122 → "one two one two two"',
    category: 'number',
  },
  {
    id: 'number',
    label: 'Number',
    description: 'Read as a cardinal number',
    example: '1850 → "eighteen hundred fifty"',
    category: 'number',
  },
  {
    id: 'ordinal',
    label: 'Ordinal',
    description: 'Read as an ordinal number',
    example: '1 → "first"',
    category: 'number',
  },
  {
    id: 'fraction',
    label: 'Fraction',
    description: 'Read as a fraction',
    example: '3/4 → "three quarters"',
    category: 'number',
  },
  { id: 'spell', label: 'Spell Out', description: 'Spell each character', example: 'CIT → "C I T"', category: 'text' },
  { id: 'word', label: 'Word', description: 'Read as a word (lowercase)', example: 'CIRI → "ciri"', category: 'text' },
  {
    id: 'state',
    label: 'State',
    description: 'Read state abbreviation as full name',
    example: 'IL → "Illinois"',
    category: 'text',
  },
  {
    id: 'year',
    label: 'Year',
    description: 'Read as a year',
    example: '2025 → "twenty twenty five"',
    category: 'datetime',
  },
  {
    id: 'date',
    label: 'Date',
    description: 'Read as a date',
    example: '01/15/2025 → date reading',
    category: 'datetime',
  },
  { id: 'time', label: 'Time', description: 'Read as a time', example: '3:30pm → time reading', category: 'datetime' },
  {
    id: 'phone',
    label: 'Phone',
    description: 'Read as a phone number',
    example: '(312) 555-1234',
    category: 'contact',
  },
  {
    id: 'address',
    label: 'Address',
    description: 'Read as a street address',
    example: '123 Main St',
    category: 'contact',
  },
];

const stateAbbreviations: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  DC: 'District of Columbia',
};

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const streetAbbreviations: Record<string, string> = {
  st: 'Street',
  str: 'Street',
  ave: 'Avenue',
  av: 'Avenue',
  blvd: 'Boulevard',
  rd: 'Road',
  dr: 'Drive',
  ln: 'Lane',
  ct: 'Court',
  pl: 'Place',
  pkwy: 'Parkway',
  hwy: 'Highway',
  fwy: 'Freeway',
  trl: 'Trail',
  ter: 'Terrace',
  cir: 'Circle',
  sq: 'Square',
  apt: 'Apartment',
  ste: 'Suite',
  bldg: 'Building',
  fl: 'Floor',
  n: 'North',
  s: 'South',
  e: 'East',
  w: 'West',
  ne: 'Northeast',
  nw: 'Northwest',
  se: 'Southeast',
  sw: 'Southwest',
};

const onesWords = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
];

const tensWords = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

const ordinalOnes = [
  'zeroth',
  'first',
  'second',
  'third',
  'fourth',
  'fifth',
  'sixth',
  'seventh',
  'eighth',
  'ninth',
  'tenth',
  'eleventh',
  'twelfth',
  'thirteenth',
  'fourteenth',
  'fifteenth',
  'sixteenth',
  'seventeenth',
  'eighteenth',
  'nineteenth',
];

const ordinalTens: Record<string, string> = {
  twenty: 'twentieth',
  thirty: 'thirtieth',
  forty: 'fortieth',
  fifty: 'fiftieth',
  sixty: 'sixtieth',
  seventy: 'seventieth',
  eighty: 'eightieth',
  ninety: 'ninetieth',
};

function wordAt(words: string[], index: number): string {
  return words[index] ?? '';
}

function numberToWords(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  if (n < 0) return `negative ${numberToWords(-n)}`;
  if (!Number.isInteger(n)) {
    const [whole = '0', frac = ''] = n.toString().split('.');
    const wholeWords = numberToWords(parseInt(whole, 10));
    const fracDigits = frac
      .split('')
      .map((d) => onesWords[parseInt(d, 10)] ?? d)
      .join(' ');
    return `${wholeWords} point ${fracDigits}`;
  }

  if (n < 20) return wordAt(onesWords, n);
  if (n < 100) {
    const tens = Math.floor(n / 10);
    const ones = n % 10;
    return ones === 0 ? wordAt(tensWords, tens) : `${wordAt(tensWords, tens)}-${wordAt(onesWords, ones)}`;
  }
  if (n < 1000) {
    const hundreds = Math.floor(n / 100);
    const rest = n % 100;
    return rest === 0
      ? `${wordAt(onesWords, hundreds)} hundred`
      : `${wordAt(onesWords, hundreds)} hundred ${numberToWords(rest)}`;
  }
  if (n < 1_000_000) {
    const thousands = Math.floor(n / 1000);
    const rest = n % 1000;
    return rest === 0
      ? `${numberToWords(thousands)} thousand`
      : `${numberToWords(thousands)} thousand ${numberToWords(rest)}`;
  }
  if (n < 1_000_000_000) {
    const millions = Math.floor(n / 1_000_000);
    const rest = n % 1_000_000;
    return rest === 0
      ? `${numberToWords(millions)} million`
      : `${numberToWords(millions)} million ${numberToWords(rest)}`;
  }
  if (n < 1_000_000_000_000) {
    const billions = Math.floor(n / 1_000_000_000);
    const rest = n % 1_000_000_000;
    return rest === 0
      ? `${numberToWords(billions)} billion`
      : `${numberToWords(billions)} billion ${numberToWords(rest)}`;
  }
  return String(n);
}

function ordinalWords(n: number): string {
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) return String(n);
  if (n < 20) return wordAt(ordinalOnes, n);
  if (n < 100) {
    const tens = Math.floor(n / 10);
    const ones = n % 10;
    if (ones === 0) return ordinalTens[wordAt(tensWords, tens)] ?? `${wordAt(tensWords, tens)}th`;
    return `${wordAt(tensWords, tens)}-${wordAt(ordinalOnes, ones)}`;
  }
  if (n < 1000) {
    const hundreds = Math.floor(n / 100);
    const rest = n % 100;
    if (rest === 0) return `${wordAt(onesWords, hundreds)} hundredth`;
    return `${wordAt(onesWords, hundreds)} hundred ${ordinalWords(rest)}`;
  }
  if (n < 1_000_000) {
    const thousands = Math.floor(n / 1000);
    const rest = n % 1000;
    if (rest === 0) return `${numberToWords(thousands)} thousandth`;
    return `${numberToWords(thousands)} thousand ${ordinalWords(rest)}`;
  }
  if (n < 1_000_000_000) {
    const millions = Math.floor(n / 1_000_000);
    const rest = n % 1_000_000;
    if (rest === 0) return `${numberToWords(millions)} millionth`;
    return `${numberToWords(millions)} million ${ordinalWords(rest)}`;
  }
  if (n < 1_000_000_000_000) {
    const billions = Math.floor(n / 1_000_000_000);
    const rest = n % 1_000_000_000;
    if (rest === 0) return `${numberToWords(billions)} billionth`;
    return `${numberToWords(billions)} billion ${ordinalWords(rest)}`;
  }
  return String(n);
}

function yearWords(content: string): string {
  const digits = content.replace(/[^0-9]/g, '');
  if (digits.length !== 4) return content;
  const n = parseInt(digits, 10);
  if (n === 0) return content;
  if (n % 1000 === 0) return `${wordAt(onesWords, Math.floor(n / 1000))} thousand`;
  if (n % 100 === 0) return `${numberToWords(Math.floor(n / 100))} hundred`;
  if (n >= 2000 && n < 2010) return `two thousand ${numberToWords(n - 2000)}`;
  const first = Math.floor(n / 100);
  const last = n % 100;
  if (last < 10) return `${numberToWords(first)} oh ${wordAt(onesWords, last)}`;
  return `${numberToWords(first)} ${numberToWords(last)}`;
}

function fractionDenominator(d: number): string {
  if (d === 100) return 'hundredth';
  if (d === 1000) return 'thousandth';
  if (d === 1_000_000) return 'millionth';
  return ordinalWords(d);
}

function fractionWords(content: string): string {
  const match = content.match(/^\s*(-?\d+)\s*\/\s*(-?\d+)\s*$/);
  if (!match) return content;
  const num = parseInt(match[1] ?? '', 10);
  const den = parseInt(match[2] ?? '', 10);
  if (den === 0) return content;
  if (num === 1 && den === 2) return 'one half';
  if (num === 1 && den === 4) return 'one quarter';
  if (num === 3 && den === 4) return 'three quarters';
  const numWords = numberToWords(num);
  if (den === 2) return `${numWords} halves`;
  if (den === 4) return `${numWords} quarters`;
  const denWord = fractionDenominator(den);
  return num === 1 ? `${numWords} ${denWord}` : `${numWords} ${denWord}s`;
}

function digitWords(content: string): string {
  return content
    .replace(/\d/g, (d) => ` ${wordAt(onesWords, parseInt(d, 10))} `)
    .replace(/\s+/g, ' ')
    .trim();
}

function spellWords(content: string): string {
  return content
    .replace(/\S/g, (c) => `${c}. `)
    .replace(/\s+/g, ' ')
    .trim();
}

function phoneWords(content: string): string {
  const digits = content.replace(/[^0-9]/g, '');
  if (digits.length === 10) {
    const a = digits.slice(0, 3);
    const b = digits.slice(3, 6);
    const c = digits.slice(6);
    return [digitWords(a), digitWords(b), digitWords(c)].join(', ');
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return [
      digitWords('1'),
      digitWords(digits.slice(1, 4)),
      digitWords(digits.slice(4, 7)),
      digitWords(digits.slice(7)),
    ].join(', ');
  }
  return digitWords(digits || content);
}

function timeWords(content: string): string {
  const match = content.match(/^\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm|AM|PM|a\.m\.|p\.m\.)?\s*$/);
  if (!match) return content;
  let hour = parseInt(match[1] ?? '', 10);
  const minute = match[2] ? parseInt(match[2], 10) : 0;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return content;
  const suffixRaw = match[3]?.toLowerCase().replace(/\./g, '');
  if (suffixRaw && hour > 12) return content;
  let suffix = '';
  if (suffixRaw === 'am' || suffixRaw === 'pm') {
    suffix = ` ${suffixRaw.toUpperCase().split('').join(' ')}`;
    if (hour === 0) hour = 12;
  } else if (hour >= 13 && hour <= 23) {
    hour -= 12;
    suffix = ' P M';
  } else if (hour === 0) {
    hour = 12;
    suffix = ' A M';
  }
  const hourWords = numberToWords(hour);
  if (minute === 0) return `${hourWords} o'clock${suffix}`;
  if (minute < 10) return `${hourWords} oh ${wordAt(onesWords, minute)}${suffix}`;
  return `${hourWords} ${numberToWords(minute)}${suffix}`;
}

function dateWords(content: string): string {
  const trimmed = content.trim();
  const dateOnly = trimmed.replace(/[T\s].*$/, '');
  const isoMatch = dateOnly.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  const slashMatch = dateOnly.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/);
  let year: number | undefined;
  let month: number;
  let day: number;
  if (isoMatch) {
    year = parseInt(isoMatch[1] ?? '', 10);
    month = parseInt(isoMatch[2] ?? '', 10);
    day = parseInt(isoMatch[3] ?? '', 10);
  } else if (slashMatch) {
    month = parseInt(slashMatch[1] ?? '', 10);
    day = parseInt(slashMatch[2] ?? '', 10);
    if (slashMatch[3]) {
      const y = parseInt(slashMatch[3], 10);
      year = slashMatch[3].length === 2 ? 2000 + y : y;
    }
  } else {
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) return content;
    year = parsed.getFullYear();
    month = parsed.getMonth() + 1;
    day = parsed.getDate();
  }

  if (month < 1 || month > 12 || day < 1 || day > 31) return content;
  const parts = [`${monthNames[month - 1]} ${ordinalWords(day)}`];
  if (year !== undefined) parts.push(yearWords(year.toString().padStart(4, '0')));
  return parts.join(' ');
}

function addressWords(content: string): string {
  return content
    .split(/\s+/)
    .filter((token) => token.length > 0)
    .map((token) => {
      const stripped = token.replace(/[.,]/g, '');
      if (/^\d+$/.test(stripped)) return digitWords(stripped);
      const lower = stripped.toLowerCase();
      if (streetAbbreviations[lower]) return streetAbbreviations[lower];
      if (stripped === '#') return 'number';
      return token;
    })
    .join(' ');
}

const transformMap: Record<string, (content: string) => string> = {
  digits: digitWords,
  number: (c) => {
    const n = Number(c.replace(/,/g, ''));
    return Number.isFinite(n) ? numberToWords(n) : c;
  },
  ordinal: (c) => {
    const n = parseInt(c, 10);
    return Number.isFinite(n) ? ordinalWords(n) : c;
  },
  fraction: fractionWords,
  spell: spellWords,
  word: (c) => c.toLowerCase(),
  state: (c) => stateAbbreviations[c.toUpperCase().trim()] ?? c,
  year: yearWords,
  date: dateWords,
  time: timeWords,
  phone: phoneWords,
  address: addressWords,
};

function processSpeechFormats(text: string): string {
  const afterSentinels = text.replace(/\x01(\w+)\(([^)]*)\)\x01/g, (_match, formatId: string, content: string) => {
    const transform = transformMap[formatId];
    return transform ? transform(content) : content;
  });
  return afterSentinels.replace(/\/(\w+)\(([^)]*)\)/g, (match, formatId: string, content: string) => {
    const transform = transformMap[formatId];
    return transform ? transform(content) : match;
  });
}

export { speechFormats, speechFormatCategories, processSpeechFormats };
export type { SpeechFormat, SpeechFormatCategory };
